import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Plus, Trash, ArrowClockwise, MagnifyingGlass, ArrowUUpLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { formatUnit } from "../lib/formatUnit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatQty = (v) => (v == null ? "\u2014" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 4 }));
const formatDateTime = (iso) => {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "\u2014" : d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const STATUS_LABEL = {
  pending: "Awaiting Store",
  under_verification: "Store Checking",
  resolved: "Back In Store",
  rejected: "Rejected",
};
const STATUS_TONE = {
  pending: "bg-[#EFF8FF] text-[#026AA2] border-[#B2DDFF]",
  under_verification: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]",
  resolved: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]",
  rejected: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]",
};

// Oct 19 2026, user's ask: "create a new page for return to store in the NPD
// store request page". Leftover material sitting in the NPD godown goes back to
// the store - NPD raises it, the STORE physically checks it in and only then
// does the SAP Goods Movement post (user's choice), always into the site's
// raw-material godown. Anything the godown holds can be returned, not only what
// came off an NPD request.
export const NpdReturnToStoreTab = ({ sites }) => {
  const [siteId, setSiteId] = useState("");
  const [stock, setStock] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState([]);
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const [returns, setReturns] = useState([]);

  useEffect(() => { if (!siteId && sites.length) setSiteId(sites[0]); }, [sites, siteId]);

  const loadStock = useCallback(async (site) => {
    if (!site) return;
    setLoadingStock(true);
    try {
      const { data } = await axios.get(`${API}/npd-store-requests/consumption/stock`, { params: { site_id: site } });
      setStock(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the NPD godown");
      setStock(null);
    } finally {
      setLoadingStock(false);
    }
  }, []);

  const loadReturns = useCallback(async (site) => {
    try {
      const { data } = await axios.get(`${API}/npd-store-requests/returns`, { params: { site_id: site || "" } });
      setReturns(data.returns || []);
    } catch {
      setReturns([]);
    }
  }, []);

  useEffect(() => {
    loadStock(siteId);
    loadReturns(siteId);
    setLines([]);
  }, [siteId, loadStock, loadReturns]);

  const visibleStock = useMemo(() => {
    const rows = stock?.items || [];
    const q = search.trim().toUpperCase();
    return q ? rows.filter((r) => r.product_id.toUpperCase().includes(q) || (r.description || "").toUpperCase().includes(q)) : rows;
  }, [stock, search]);

  const addLine = (row) => {
    if (lines.some((l) => l.product_id === row.product_id)) return toast.error(`${row.product_id} is already on the list`);
    setLines((prev) => [...prev, {
      product_id: row.product_id, description: row.description, available: row.qty, uom: row.uom,
      quantity: String(row.qty),
    }]);
  };

  const missingField = useMemo(() => {
    if (!siteId) return "Select a site";
    if (lines.length === 0) return "Add at least one item from the NPD godown";
    if (lines.some((l) => !(Number(l.quantity) > 0))) return "Every line needs a quantity";
    const over = lines.find((l) => Number(l.quantity) > l.available);
    if (over) return `${over.product_id}: only ${formatQty(over.available)} ${formatUnit(over.uom)} in the godown`;
    if (reason.trim().length < 5) return "Say in a few words why it is going back";
    return "";
  }, [siteId, lines, reason]);

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/npd-store-requests/returns`, {
        site_id: siteId,
        reason: reason.trim(),
        lines: lines.map((l) => ({
          product_id: l.product_id, quantity: Number(l.quantity), uom: l.uom, description: l.description,
        })),
      });
      toast.success(`${data._id} sent to the store`, { description: "The store checks the material in, then the stock moves in SAP." });
      setLines([]);
      setReason("");
      loadReturns(siteId);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not raise this return");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="npd-return-tab">
      <div className="bg-white border border-[#D0D5DD] rounded-sm">
        <div className="px-4 py-3 border-b border-[#EAECF0] flex items-center gap-3 flex-wrap">
          <ArrowUUpLeft size={16} className="text-[#0E7C86]" />
          <h2 className="text-sm font-bold text-[#101828]">Return To Store</h2>
          <Badge variant="outline" className="text-[10px] bg-[#EFF8FF] text-[#026AA2] border-[#B2DDFF]" data-testid="npd-return-flow-badge">
            STORE CONFIRMS, THEN SAP POSTS
          </Badge>
          <span className="text-xs text-[#667085]" data-testid="npd-return-context">
            {stock ? `${stock.godown} \u2192 ${siteId}-RM (raw material)` : "\u2014"}
            {stock?.as_of ? ` \u00b7 stock as of ${formatDateTime(stock.as_of)}` : ""}
          </span>
          <Button variant="outline" size="sm" className="h-8 bg-white ml-auto" onClick={() => loadStock(siteId)} data-testid="npd-return-refresh-stock">
            <ArrowClockwise size={14} className="mr-1.5" /> Refresh Stock
          </Button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Site *</Label>
              <Select value={siteId} onValueChange={setSiteId}>
                <SelectTrigger className="h-9 bg-white" data-testid="npd-return-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {sites.map((s) => <SelectItem key={s} value={s} data-testid={`npd-return-site-option-${s}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 sm:col-span-2 lg:col-span-3">
              <Label className="text-xs font-bold text-[#344054]">Why is it going back? *</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="e.g. trial finished, excess issued, wrong material"
                data-testid="npd-return-reason-input"
              />
              <p className="text-[11px] text-[#667085]">The store and the return report both show this.</p>
            </div>
          </div>

          <div className="bg-[#F0FDF9] border border-[#AAF0D1] rounded-sm px-3 py-2 text-[12px] text-[#027A48]" data-testid="npd-return-flow-note">
            The store checks the material back in and only then does the stock move in SAP, from <strong>{stock?.godown || "the NPD godown"}</strong> into the site's <strong>raw-material godown</strong>.
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-[#D0D5DD] rounded-sm">
              <div className="px-3 py-2 border-b border-[#EAECF0] flex items-center gap-2">
                <MagnifyingGlass size={14} className="text-[#667085]" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search the NPD godown..."
                  className="h-7 border-0 shadow-none focus-visible:ring-0 px-0"
                  data-testid="npd-return-stock-search"
                />
                <span className="text-[11px] text-[#667085] whitespace-nowrap">{visibleStock.length} item(s)</span>
              </div>
              <div className="max-h-72 overflow-auto">
                <table className="w-full text-[12px]" data-testid="npd-return-stock-table">
                  <thead className="bg-[#F9FAFB] sticky top-0">
                    <tr>
                      {["Product", "Description", "In Godown", ""].map((h) => (
                        <th key={h} className="px-2 py-1.5 text-left font-bold text-[#475467] border-b border-[#EAECF0]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loadingStock && <tr><td colSpan={4} className="px-2 py-6 text-center text-[#98A2B3]">Reading the godown...</td></tr>}
                    {!loadingStock && visibleStock.length === 0 && (
                      <tr><td colSpan={4} className="px-2 py-6 text-center text-[#98A2B3]" data-testid="npd-return-stock-empty">
                        {stock?.godown ? `${stock.godown} is empty - there is nothing to send back.` : "Select a site"}
                      </td></tr>
                    )}
                    {visibleStock.map((r) => (
                      <tr key={r.product_id} className="border-b border-[#F2F4F7] last:border-b-0" data-testid={`npd-return-stock-row-${r.product_id}`}>
                        <td className="px-2 py-1.5 font-mono font-bold">{r.product_id}</td>
                        <td className="px-2 py-1.5 text-[#475467]">{r.description || "\u2014"}</td>
                        <td className="px-2 py-1.5 tabular-nums">{formatQty(r.qty)} {formatUnit(r.uom)}</td>
                        <td className="px-2 py-1.5 text-right">
                          <Button variant="outline" size="sm" className="h-6 bg-white text-[11px]" onClick={() => addLine(r)} data-testid={`npd-return-add-${r.product_id}`}>
                            <Plus size={12} className="mr-1" /> Add
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="border border-[#D0D5DD] rounded-sm">
              <div className="px-3 py-2 border-b border-[#EAECF0]">
                <h3 className="text-xs font-bold text-[#344054]">Sending Back</h3>
              </div>
              <div className="max-h-72 overflow-auto">
                <table className="w-full text-[12px]" data-testid="npd-return-lines-table">
                  <thead className="bg-[#F9FAFB] sticky top-0">
                    <tr>
                      {["Product", "In Godown", "Return Qty", ""].map((h) => (
                        <th key={h} className="px-2 py-1.5 text-left font-bold text-[#475467] border-b border-[#EAECF0]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length === 0 && (
                      <tr><td colSpan={4} className="px-2 py-6 text-center text-[#98A2B3]" data-testid="npd-return-lines-empty">Add items from the godown on the left.</td></tr>
                    )}
                    {lines.map((l, i) => (
                      <tr key={l.product_id} className="border-b border-[#F2F4F7] last:border-b-0" data-testid={`npd-return-line-row-${l.product_id}`}>
                        <td className="px-2 py-1.5 font-mono font-bold">{l.product_id}</td>
                        <td className="px-2 py-1.5 tabular-nums text-[#475467]">{formatQty(l.available)} {formatUnit(l.uom)}</td>
                        <td className="px-2 py-1.5">
                          <Input
                            type="number"
                            min="0"
                            step="any"
                            value={l.quantity}
                            onChange={(e) => setLines((prev) => prev.map((x, xi) => (xi === i ? { ...x, quantity: e.target.value } : x)))}
                            className={`h-7 w-28 ${Number(l.quantity) > l.available ? "border-[#FDA29B]" : ""}`}
                            data-testid={`npd-return-qty-input-${l.product_id}`}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <Button variant="outline" size="sm" className="h-6 bg-white text-[#B42318]" onClick={() => setLines((prev) => prev.filter((_, xi) => xi !== i))} data-testid={`npd-return-remove-${l.product_id}`}>
                            <Trash size={12} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button type="button" disabled={!!missingField || sending} onClick={submit} data-testid="npd-return-submit-button">
              {sending ? "Sending..." : "Send Back To Store"}
            </Button>
            <span className="text-xs text-[#667085]" data-testid="npd-return-hint">
              {missingField || `${lines.length} line(s) ready \u00b7 the store will check them in`}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#D0D5DD] rounded-sm">
        <div className="px-4 py-3 border-b border-[#EAECF0] flex items-center gap-3">
          <h2 className="text-sm font-bold text-[#101828]">My NPD Returns</h2>
          <Button variant="outline" size="sm" className="h-8 bg-white ml-auto" onClick={() => loadReturns(siteId)} data-testid="npd-return-refresh-list">
            <ArrowClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="npd-return-list-table">
            <thead className="bg-[#F9FAFB]">
              <tr>
                {["Return ID", "Raised", "Site", "Items", "Reason", "Status", "SAP Movement"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-bold text-[#475467] border-b border-[#EAECF0]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {returns.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-[#98A2B3]" data-testid="npd-return-list-empty">No returns raised yet.</td></tr>
              )}
              {returns.map((r) => {
                const movement = (r.items || []).map((it) => (it.sap_goods_movement || {}).external_id).find(Boolean);
                return (
                  <tr key={r._id} className="border-b border-[#F2F4F7] last:border-b-0" data-testid={`npd-return-row-${r._id}`}>
                    <td className="px-3 py-2 font-mono font-bold text-[#175CD3]">{r._id}</td>
                    <td className="px-3 py-2 text-[#475467]">{formatDateTime(r.created_at)}</td>
                    <td className="px-3 py-2">{r.site_id}</td>
                    <td className="px-3 py-2">
                      {(r.items || []).map((it) => (
                        <span key={it.product_id} className="block font-mono">{it.product_id} · {formatQty(it.return_qty)} {formatUnit(it.unit_of_measure)}</span>
                      ))}
                    </td>
                    <td className="px-3 py-2 text-[#475467] max-w-[220px]">
                      {(r.items || [])[0]?.remarks || "\u2014"}
                      {r.status === "rejected" && r.rejection_reason && (
                        <span className="block text-[11px] text-[#B42318] mt-1" data-testid={`npd-return-rejection-${r._id}`}>
                          Store rejected: {r.rejection_reason}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[r.status] || ""}`} data-testid={`npd-return-status-${r._id}`}>
                        {STATUS_LABEL[r.status] || r.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 font-mono text-[#027A48]">{movement || "\u2014"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
